import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Notification } from '../../components/Notification';
import { Announcement, FLOCK_CHANNELS, SummaryConfig } from '../../utils/apiEndpoints';
import clientService from '../../apiClient';
import aiApiClient from '../../apiClient/ai-api-client';

const initialState = {
  loading: {
    flockWebHooks: false,
    addFlockWebHook: false,
    summary: false,
    sendFlock: false,
    summaryConfiguration: false,
  },
  flockWebHooks: [],
  summary: null,
  summaryConfiguration: null,
};

const announcementSlice = createSlice({
  name: 'announcement',
  initialState,
  reducers: {
    setSummary: (state, action) => {
      state.summary = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFlockWebHooks.pending, (state) => {
      state.loading.flockWebHooks = true;
    });
    builder.addCase(fetchFlockWebHooks.fulfilled, (state, action) => {
      state.loading.flockWebHooks = false;
      state.flockWebHooks = action.payload?.webhooks;
    });
    builder.addCase(fetchFlockWebHooks.rejected, (state, action) => {
      state.loading.flockWebHooks = false;
      Notification.error('Error happened while fetching flock channels', action?.payload?.error);
    });
    builder.addCase(addFlockWebHook.pending, (state) => {
      state.loading.addFlockWebHook = true;
    });
    builder.addCase(addFlockWebHook.fulfilled, (state, action) => {
      state.loading.addFlockWebHook = false;
      state.flockWebHooks = [...state.flockWebHooks, action.payload.data];
    });
    builder.addCase(addFlockWebHook.rejected, (state, action) => {
      state.loading.addFlockWebHook = false;
      Notification.error('Error happened while adding flock channels', action?.payload?.error);
    });
    builder.addCase(generateCustomSummary.pending, (state) => {
      state.loading.summary = true;
    });
    builder.addCase(generateCustomSummary.fulfilled, (state, action) => {
      state.loading.summary = false;
      state.summary = action.payload?.response?.content?.content;
    });
    builder.addCase(generateCustomSummary.rejected, (state, action) => {
      state.loading.summary = false;
      Notification.error('Error happened while generating summary', action?.payload?.error);
    });
    builder.addCase(postFlockMessage.pending, (state) => {
      state.loading.sendFlock = true;
    });
    builder.addCase(postFlockMessage.fulfilled, (state, action) => {
      state.loading.sendFlock = false;
      state.summary = '';
    });
    builder.addCase(postFlockMessage.rejected, (state, action) => {
      state.loading.sendFlock = true;
      Notification.error('Error happened while sending announcement', action?.payload?.error);
    });
    builder.addCase(getSummaryConfig.pending, (state) => {
      state.loading.summaryConfiguration = true;
    });
    builder.addCase(getSummaryConfig.fulfilled, (state, action) => {
      state.loading.summaryConfiguration = false;
      state.summaryConfiguration = action.payload?.data;
    });
    builder.addCase(getSummaryConfig.rejected, (state, action) => {
      state.loading.summaryConfiguration = true;
      Notification.error('Error happened while fetching summary configuration', action?.payload?.error);
    });
  },
});

export const fetchFlockWebHooks = createAsyncThunk('cms/fetchFlockWebHooks', async (_, { rejectWithValue }) => {
  try {
    const response = await clientService.get(FLOCK_CHANNELS);
    return response?.data;
  } catch (err) {
    return rejectWithValue({ error: err?.data?.error?.message });
  }
});

export const addFlockWebHook = createAsyncThunk('cms/addFlockWebHook', async (body, { rejectWithValue }) => {
  try {
    const response = await clientService.post(FLOCK_CHANNELS, body);
    return response;
  } catch (err) {
    return rejectWithValue({ error: err?.data?.error?.message });
  }
});

export const postFlockMessage = createAsyncThunk('cms/postFlockMessage', async (body, { rejectWithValue }) => {
  try {
    const response = await clientService.post(`${Announcement}/flock`, body);
    return response;
  } catch (err) {
    return rejectWithValue({ error: err?.data?.error?.message });
  }
});

export const generateCustomSummary = createAsyncThunk(
  'cms/generateCustomSummary',
  async ({ documentData, tone, focus, audience, rawPrompt, task }, { rejectWithValue }) => {
    try {
      const body = { document_data: documentData };
      if (tone) body['tone'] = tone;
      if (focus) body['focus'] = focus;
      if (audience) body['audience'] = audience;
      if (rawPrompt) body['raw_prompt'] = rawPrompt;
      if (task) body['task'] = task;
      const response = await aiApiClient.post(`/ai/documents/summarize`, body);
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const updateSummaryConfig = createAsyncThunk('cms/updateSummaryConfig', async (body, { rejectWithValue }) => {
  try {
    const response = await clientService.post(SummaryConfig, body);
    return response;
  } catch (err) {
    return rejectWithValue({ error: err?.data?.error?.message });
  }
});

export const getSummaryConfig = createAsyncThunk('cms/getSummaryConfig', async (_, { rejectWithValue }) => {
  try {
    const response = await clientService.get(SummaryConfig);
    return response;
  } catch (err) {
    return rejectWithValue({ error: err?.data?.error?.message });
  }
});

export default announcementSlice.reducer;
export const { setSummary } = announcementSlice.actions;
